import React, { useState } from 'react';
import { HeartPulse, Plus, Trash2 } from 'lucide-react';
import {
  Alert, Badge, Button, Card, Disclaimer, Empty, Field, Input, Page, PageHead, Select,
} from '../components/ui';
import { shortDate, titleise } from '../lib/format';

const SEVERITIES = ['mild', 'moderate', 'severe'];
const TONE = { mild: 'muted', moderate: 'warn', severe: 'crit' };
const BLANK = { name: '', diagnosed_date: '', severity: 'mild', medications: '' };

export default function Health({ conditions, onAdd, onDelete }) {
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true); setError('');
    try {
      await onAdd({
        name: form.name.trim(),
        severity: form.severity,
        diagnosed_date: form.diagnosed_date || null,
        medications: form.medications.trim() || null,
      });
      setForm(BLANK);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  return (
    <Page>
      <PageHead title="Health Conditions">
        Conditions and medications you record here are given to the AI assistant as context, so its
        answers account for them.
      </PageHead>

      <Card title="Add a condition">
        <form onSubmit={submit}>
          {error && <div style={{ marginBottom: 12 }}><Alert tone="crit">{error}</Alert></div>}
          <div className="grid" style={{ gridTemplateColumns: 'minmax(200px, 2fr) minmax(120px, 1fr) minmax(140px, 1fr)' }}>
            <Field label="Condition">
              <Input placeholder="Mild hypertension" value={form.name} onChange={set('name')} required />
            </Field>
            <Field label="Severity">
              <Select options={SEVERITIES.map((s) => ({ value: s, label: titleise(s) }))}
                value={form.severity} onChange={set('severity')} />
            </Field>
            <Field label="Diagnosed">
              <Input type="date" value={form.diagnosed_date} onChange={set('diagnosed_date')} />
            </Field>
          </div>
          <div style={{ marginTop: 14 }}>
            <Field label="Medications" hint="Name and dose, if any">
              <Input placeholder="Lisinopril 5 mg once daily" value={form.medications} onChange={set('medications')} />
            </Field>
          </div>
          <Button variant="primary" icon={Plus} type="submit" disabled={busy || !form.name.trim()}
            loading={busy} style={{ marginTop: 16 }}>
            Add condition
          </Button>
        </form>
      </Card>

      <Card title={`Recorded (${conditions.length})`} flush>
        {conditions.length === 0 ? (
          <Empty icon={HeartPulse} title="Nothing recorded">
            If you have an ongoing condition, adding it here makes the assistant's advice more relevant.
          </Empty>
        ) : (
          conditions.map((c) => (
            <div className="item" key={c.id}>
              <div className="grow">
                <div className="item-title">{c.name}</div>
                <div className="item-sub">
                  {c.medications || 'No medication recorded'}
                  {c.diagnosed_date && ` · diagnosed ${shortDate(c.diagnosed_date)}`}
                </div>
              </div>
              <Badge tone={TONE[c.severity] || 'muted'}>{titleise(c.severity)}</Badge>
              <Button size="sm" variant="danger" icon={Trash2}
                onClick={() => onDelete(c.id)} aria-label={`Delete ${c.name}`} />
            </div>
          ))
        )}
        <div style={{ padding: '12px 16px 14px' }}>
          <Disclaimer>
            This is a personal record, not a medical one. It is never shared and does not replace your
            clinical notes.
          </Disclaimer>
        </div>
      </Card>
    </Page>
  );
}
